import { Permission, Role } from '@ekkogmbh/apisdk';
import { Add, AdminPanelSettingsOutlined, Save } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { RolePermissionSelect } from './RolePermissionSelect';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import useEkko from 'src/Common/Providers/EkkoProvider';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

interface Props {
  role?: Role;
  /** All permissions selectable for this role (fetched once from the API). */
  availablePermissions: Permission[];
  saveHandler: (role: Partial<Role>) => void;
  onClose: () => void;
}

export const RolesAdminDrawer = ({ role, availablePermissions, saveHandler, onClose }: Props): React.JSX.Element => {
  const { currentUser } = useEkko();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const touch = (field: string): void => setTouchedFields((prev) => ({ ...prev, [field]: true }));

  const isEdit = role !== undefined;

  const reset = useCallback(() => {
    setName(role?.name ?? '');
    setCoordinate(role?.coordinate ?? '');
    setSelectedPermissions(role?.permissions ?? []);
    setTouchedFields({});
  }, [role]);

  useEffect(() => {
    reset();
  }, [reset]);

  const payload = useMemo((): Partial<Role> | undefined => {
    // only supeadmin may create role without node
    if (name === '' || coordinate === '') {
      return undefined;
    }

    return {
      id: role?.id,
      name,
      coordinate,
      permissions: selectedPermissions,
    };
  }, [role, name, coordinate, selectedPermissions, currentUser]);

  const isSavable = useMemo(
    () => payload !== undefined && selectedPermissions.length > 0,
    [payload, selectedPermissions],
  );

  return (
    <SectionDrawerContent
      onClose={onClose}
      header={
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AdminPanelSettingsOutlined color="action" />
          <Stack spacing={0}>
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? 'Edit role' : 'Add role'}
            </Typography>
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                {role?.name}
              </Typography>
            )}
          </Stack>
        </Stack>
      }
      actions={{
        reset: { onClick: reset, disabled: payload === undefined },
        confirm: isEdit
          ? {
              label: 'Save',
              icon: <Save />,
              onClick: () => saveHandler(payload!),
              disabled: !isSavable,
            }
          : {
              label: 'Create',
              icon: <Add />,
              onClick: () => saveHandler(payload!),
              disabled: !isSavable,
            },
      }}
    >
      <Stack spacing={4}>
        {/* Identity */}
        <Stack spacing={1.5}>
          <SectionHeader title="Identity" />
          <StyledTextField
            label="Role name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => touch('name')}
            error={touchedFields.name && name === ''}
          />
          <CoordinateInput value={coordinate} onChange={setCoordinate} trailingDelimiter={false} />
        </Stack>

        {/* Permissions */}
        <Stack spacing={1.5}>
          <SectionHeader title="Permissions" />
          <Typography variant="caption" color="text.secondary">
            Select one or more permissions to grant this role.
          </Typography>
          <RolePermissionSelect
            width="100%"
            label="Permissions"
            role={payload}
            allPermissions={availablePermissions}
            saveHandler={(change) => setSelectedPermissions([...(change?.permissions ?? [])])}
            noDialog
          />
        </Stack>
      </Stack>
    </SectionDrawerContent>
  );
};
